import React, { useState } from "react";
import axios from "axios";
import { Box, FormControl, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import userAuthenticationConfig from "../../../../utils/userAuthenticationConfig";
import TelegramPairSelector, { togglePair } from "./TelegramPairSelector";
import DefaultButton from "../../../styles/materialButtonStyle";
import MaterialDialog from "../../../styles/materialDialog";
import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../../styles/styledDialog";

const MANAGER_TYPES = ["cash", "bank", "crypto"];
const EMPTY_FORM    = { projectId: "", type: "crypto", connectionId: "", pairs: [] };

const TelegramAddConnectionDialog = ({ setNotification, onAdded, t }) => {
  const [open, setOpen]                         = useState(false);
  const [form, setForm]                         = useState(EMPTY_FORM);
  const [projects, setProjects]                 = useState([]);
  const [projectConnections, setProjectConnections] = useState([]);
  const [availablePairs, setAvailablePairs]     = useState([]);
  const [loadingPairs, setLoadingPairs]         = useState(false);

  const handleOpen = () => {
    setForm(EMPTY_FORM);
    setProjects([]);
    setProjectConnections([]);
    setAvailablePairs([]);
    setOpen(true);
    axios.get("/api/projects?itemsPerPage=200", userAuthenticationConfig())
      .then(r => setProjects(r.data["hydra:member"] || []))
      .catch(() => {});
  };

  const handleProjectSelect = (projectId) => {
    setForm(f => ({ ...f, projectId, connectionId: "", pairs: [] }));
    setProjectConnections([]);
    setAvailablePairs([]);
    if (!projectId) return;
    axios.get(`/api/connections?project.id=${projectId}&pagination=false`, userAuthenticationConfig())
      .then(r => setProjectConnections(r.data["hydra:member"] || []))
      .catch(() => setProjectConnections([]));
  };

  const handleConnectionSelect = (connectionId) => {
    setForm(f => ({ ...f, connectionId, pairs: [] }));
    setAvailablePairs([]);
    if (!connectionId) return;
    const conn = projectConnections.find(c => String(c.id) === String(connectionId));
    if (!conn?.service?.id) return;
    setLoadingPairs(true);
    axios.get(`/api/service-relationships?service.id=${conn.service.id}`, userAuthenticationConfig())
      .then(r => setAvailablePairs(r.data["hydra:member"] || []))
      .catch(() => setAvailablePairs([]))
      .finally(() => setLoadingPairs(false));
  };

  const handleAdd = () => {
    if (!form.projectId) {
      setNotification({ visible: true, type: "error", message: t("connections.errors.projectRequired") });
      return;
    }
    if (!form.connectionId) {
      setNotification({ visible: true, type: "error", message: t("connections.errors.connectionRequired") });
      return;
    }
    axios.post(
      `/api/telegram/projects/${form.projectId}/connections`,
      { type: form.type, connectionId: form.connectionId, pairs: form.pairs },
      userAuthenticationConfig()
    ).then(r => {
      if (r.status === 201) {
        setOpen(false);
        setNotification({ visible: true, type: "success", message: t("connections.success.added") });
        onAdded();
      }
    }).catch(error => {
      setNotification({ visible: true, type: "error", message: error?.response?.data?.message || t("connections.errors.add") });
    });
  };

  return (
    <>
      <DefaultButton color="success" onClick={handleOpen}>
        {t("connections.add")}
      </DefaultButton>

      <MaterialDialog open={open}>
        <StyledDialogWrapper>
          <StyledDialogHead>
            {t("connections.dialog.addTitle")}
          </StyledDialogHead>
          <StyledDialogContent>
            <FormControl fullWidth>
              <Autocomplete
                options={projects}
                value={projects.find(p => String(p.id) === form.projectId) || null}
                onChange={(event, project) => handleProjectSelect(project ? String(project.id) : "")}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label={t("connections.dialog.project")} />
                )}
                blurOnSelect
                disableClearable
              />
            </FormControl>
            <FormControl fullWidth>
              <Autocomplete
                options={MANAGER_TYPES}
                value={form.type}
                onChange={(event, type) => setForm(f => ({ ...f, type: type || "crypto" }))}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField {...params} label={t("connections.dialog.managerType")} />
                )}
                blurOnSelect
                disableClearable
              />
            </FormControl>
            <FormControl fullWidth>
              <Autocomplete
                options={projectConnections}
                value={projectConnections.find(c => String(c.id) === form.connectionId) || null}
                onChange={(event, conn) => handleConnectionSelect(conn ? String(conn.id) : "")}
                getOptionLabel={(option) => `${option.name || option.id}${option.service?.name ? ` — ${option.service.name}` : ""}`}
                renderInput={(params) => (
                  <TextField {...params} label={t("connections.dialog.connection")} />
                )}
                blurOnSelect
                disableClearable
                disabled={!form.projectId}
              />
            </FormControl>
            {form.connectionId && (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">{t("connections.dialog.pairsLabel")}</Typography>
                <TelegramPairSelector
                  available={availablePairs}
                  selected={form.pairs}
                  onToggle={(cId, psId) => setForm(f => ({ ...f, pairs: togglePair(f.pairs, cId, psId) }))}
                  loading={loadingPairs}
                  t={t}
                />
              </Box>
            )}
          </StyledDialogContent>
          <StyledDialogAction col="2">
            <DefaultButton onClick={() => setOpen(false)} color="danger">
              {t("connections.dialog.cancel")}
            </DefaultButton>
            <DefaultButton onClick={handleAdd} color="success">
              {t("connections.dialog.add")}
            </DefaultButton>
          </StyledDialogAction>
        </StyledDialogWrapper>
      </MaterialDialog>
    </>
  );
};

export default TelegramAddConnectionDialog;